import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics component for tracking page views and events
export const GoogleAnalytics: React.FC = () => {
  const location = useLocation();

  // Initialize Google Analytics
  useEffect(() => {
    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    
    if (!GA_MEASUREMENT_ID) {
      console.warn('Google Analytics Measurement ID not found. Add REACT_APP_GA_MEASUREMENT_ID to your environment variables.');
      return;
    }

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag
    script.onload = () => {
      // @ts-ignore
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        // @ts-ignore
        window.dataLayer.push(args);
      }
      window.gtag = gtag;
      
      window.gtag('js', new Date());
      window.gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false, // We'll handle page views manually
        anonymize_ip: true, // Privacy compliance
        allow_google_signals: false, // Privacy compliance
      });
    };

    return () => {
      // Cleanup
      document.head.removeChild(script);
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;
    
    if (window.gtag && GA_MEASUREMENT_ID) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  return null; // This component doesn't render anything
};

// Analytics event tracking functions
export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (window.gtag) {
    window.gtag('event', eventName, parameters);
  }
};

// Common ecommerce events
export const trackPurchase = (transactionId: string, value: number, currency: string = 'USD', items: any[] = []) => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value: value,
    currency: currency,
    items: items
  });
};

export const trackAddToCart = (itemId: string, itemName: string, category?: string, quantity: number = 1, value?: number) => {
  trackEvent('add_to_cart', {
    currency: 'USD',
    value: value,
    items: [{
      item_id: itemId,
      item_name: itemName,
      category: category,
      quantity: quantity,
      price: value
    }]
  });
};

export const trackViewItem = (itemId: string, itemName: string, category?: string, value?: number) => {
  trackEvent('view_item', {
    currency: 'USD',
    value: value,
    items: [{
      item_id: itemId,
      item_name: itemName,
      category: category,
      price: value
    }]
  });
};

export const trackSearch = (searchTerm: string) => {
  trackEvent('search', {
    search_term: searchTerm
  });
};

export const trackBeginCheckout = (value: number, currency: string = 'USD', items: any[] = []) => {
  trackEvent('begin_checkout', {
    currency: currency,
    value: value,
    items: items
  });
};

// Extend the Window interface to include gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

export default GoogleAnalytics;
